import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {VisualSizesEnum} from '../../styles/commonStyles';
import {rotate360Animation} from '../../styles/mixins';
import {makeSizeConstants} from '../../styles/styleHelpers';

/*
 * Props.
 */

export enum LoaderVariantsEnum {
  BASE = 'BASE',
  GREY = 'GREY',
}

export interface LoaderProps {
  className?: string;
  size?: VisualSizesEnum;
  variant?: LoaderVariantsEnum;
  isEnabled?: boolean;
}

/*
 * Style.
 */

const sizes = makeSizeConstants(16, 20, 40);

interface LoaderStyleProps {
  $variant: LoaderVariantsEnum;
  $size: VisualSizesEnum;
}

const mask = `
  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>
    <circle fill='none' stroke='white' stroke-width='15' stroke-linecap='round' stroke-dasharray='400 1000' stroke-dashoffset='-10' cx='100' cy='100' r='70' />
  </svg>
`;

/**
 * Explanation on how this works:
 *
 * We have a background that is a conic radiant, going from <color> to transparent
 * On top of this, we have a `mask` that is a SVG circle, with stroke-dasharray, which stops the stroke at a certain angle, with stroke-linecap=round for the rounded corners
 * stroke-dashoffset is used to offset the start of the stroke, so that the circle is not cut off at the start.
 *
 * The whole thing rotates through a CSS animation.
 */
const StyledLoader = styled.div<LoaderStyleProps>`
  width: ${(p) => sizes[p.$size]}px;
  aspect-ratio: 1;

  ${rotate360Animation()}

  ${(p) => {
    const color =
      p.$variant === LoaderVariantsEnum.GREY
        ? p.theme.accent.gray.highlight
        : p.theme.background.fill.primary;

    return css`
      background: conic-gradient(
        from 90deg,
        rgb(from ${color} r g b / 0) 0deg,
        rgb(from ${color} r g b / 1) 360deg
      );
    `;
  }}

  mask: url(${encodeSvgImage(mask)});
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;

  &[aria-disabled='true'] {
    animation: none;
  }
`;

/*
 * Component.
 */

export const Loader: FC<LoaderProps> = ({
  isEnabled = true,
  size = VisualSizesEnum.LARGE,
  variant = LoaderVariantsEnum.BASE,
}) => (
  <StyledLoader data-testid="loader" aria-disabled={isEnabled === false} $variant={variant} $size={size} />
);

/*
 * Helpers.
 */

function encodeSvgImage(image: string) {
  const encoded = encodeURIComponent(image).replace(/%[\dA-F]{2}/g, specialHexEncode);
  return `"data:image/svg+xml,${encoded}"`;
}

function specialHexEncode(match: string) {
  // Browsers tolerate these characters, and they're frequent
  switch (match) {
    case '%20':
      return ' ';
    case '%3D':
      return '=';
    case '%3A':
      return ':';
    case '%2F':
      return '/';
    default:
      return match.toLowerCase(); // compresses better
  }
}
